import { useEffect } from 'react';
import { useQuery } from '@tanstack/react-query';
import { queryClient, queryKeys } from './query-client';
import { useWorkspaces } from './workspaces';
import { fetchTasks, unsettledTasksRefetchInterval, type MaestroAction, type WebTask } from './tasks';
import { useAgentTaskInvalidation } from './agent-task-invalidation';
import { buildIslandQuickView, type IslandQuickView, type QuickViewTask } from './island-quickview';

/**
 * The Maestro's in-flight non-task work, as the account-wide `/api/tasks` last reported it. The entry is
 * never fetched on its own — `fetchTasks` writes it as a sibling of the rows — so this is a cache
 * OBSERVER (`enabled: false`): it re-renders when a fetch lands and holds `[]` until one has. Absent ≡
 * empty, so an old server (or a not-yet-loaded list) claims no actions — never blue from ignorance.
 */
export function useMaestroActions(): MaestroAction[] {
  const { data } = useQuery<MaestroAction[]>({
    queryKey: queryKeys.maestroActions,
    enabled: false,
  });
  return data ?? [];
}

export type IslandQuickViewState = { status: 'loading' } | { status: 'ready'; data: IslandQuickView } | { status: 'error' };

/**
 * The island quick-view's data — the peek the popover (web) / sheet (native) render.
 *
 * It reads the **account-wide `/api/tasks` list**, on the SAME React Query key the Tasks screen renders
 * from (`queryKeys.tasks(undefined)`), so the bubble is a projection of that screen rather than a
 * parallel list. It used to fan out over `/api/workspace-tasks?workspaceId=` once per workspace, and that
 * quietly made it a DIFFERENT list: that route returns raw AgentTasks scoped to a workspace plus one
 * level of child projects, so the bubble could never show an inbound outreach row (they have no
 * workspace), nor a task whose `workspaceId` is empty or whose Space is missing from the roster. A row on
 * the screen that cannot appear in the bubble is the same class of bug as the two disagreeing about order.
 *
 * Reading one account-wide entry also costs FEWER requests than the fan-out it replaces — one call
 * instead of one per Space — but it is a heavier call, and the pill is mounted on every screen: this is
 * now the app's ambient task read. Hence `staleTime` + `refetchOnMount: false`; freshness comes from the
 * agent-task duplexer below, which is the signal that actually means something changed — under
 * `useTasks`'s own floor (`unsettledTasksRefetchInterval`), which this observer takes for the reason that
 * timer exists: it was added because a dropped duplexer frame left THE PILL claiming work that had
 * finished, and the pill is mounted everywhere while `/tasks` is one screen. A floor only the Tasks screen
 * carried would leave the most visible reader of this entry the stalest one. Unsettled-gated and
 * foreground-only there, so a finished list still costs nothing.
 *
 * ⚠️ One consequence worth naming: `/api/tasks` WRITES on the read path (`reconcileAbandoned` settles
 * tasks whose loop died), so that reconciliation now runs from every screen rather than only from
 * `/tasks`. It is idempotent and finds nothing on the second pass, but it belongs off a GET; moving it is
 * tracked separately rather than folded into this change.
 */
export function useIslandQuickView(): IslandQuickViewState {
  const workspacesState = useWorkspaces();
  const workspaces = workspacesState.status === 'ready' ? workspacesState.workspaces : [];
  // The Maestro's non-task work, folded into the blue card's rows and both working counts. Rides the same
  // fetch as the rows, so the two cannot be a poll apart.
  const maestroActions = useMaestroActions();

  const query = useQuery({
    queryKey: queryKeys.tasks(undefined),
    queryFn: () => fetchTasks(),
    staleTime: 60_000,
    refetchOnMount: false,
    refetchInterval: (data) => unsettledTasksRefetchInterval(data as WebTask[] | undefined),
    refetchIntervalInBackground: false,
  });

  // Freshness comes from here, not from mount — but through ONE shared listener that INVALIDATES, not a
  // per-instance `refetch()`. This used to be its own listener, and on `/tasks` it fired alongside
  // `useTasks()`'s own for a single duplexer event; since `refetch()` defaults to `cancelRefetch: true`
  // the two cancelled each other, so the screen issued two `/api/tasks` calls per event with the first
  // abandoned mid-flight. Multiply that by the pill mounting on every route and by `useIslandState` /
  // `useIslandAgentStarted` each instantiating this hook, and one frame became the measured 15-calls-in-89ms
  // storm. Invalidation is idempotent per key, so the collapse is structural rather than a tuning knob.
  useAgentTaskInvalidation();

  // Data first: React Query keeps the last good list alongside an `error` status on a failed REFETCH, and
  // showing the rows we have beats blanking the bubble over a transient failure. Error only before the
  // first success; loading until then.
  if (!query.data) return query.status === 'error' ? { status: 'error' } : { status: 'loading' };

  const names = new Map(workspaces.map((workspace) => [workspace.id, workspace.name]));
  const tasks: QuickViewTask[] = query.data.map((task: WebTask) => ({
    id: task.id ?? '',
    title: (task.title ?? '').trim(),
    status: task.status ?? '',
    agentId: task.agentId,
    // Both empty on an outreach row (an Outreach belongs to no Space) — the row simply shows no Space.
    workspaceId: task.workspaceId ?? '',
    workspaceName: (task.workspaceId && names.get(task.workspaceId)) || '',
    summary: task.summary?.trim() || undefined,
    updatedDate: task.updatedDate,
    // Carried so the peek folds consecutive tasks of one origin exactly where the Tasks screen does —
    // a routine's runs, or a goal's laid-out plan.
    pulseIdentityId: task.pulseIdentityId || undefined,
    goalId: task.goalId || undefined,
    // The two fields the derived "laid out and never started" rule reads (`isNotStartedTask`), so the
    // bubble cannot show "In progress" on a row the Tasks screen shows as Not started.
    notBefore: task.notBefore || undefined,
    agentConversationId: task.agentConversationId || undefined,
    // Carried so the row can title and open itself the way `TaskCard` does — an outreach has no task
    // detail behind it, so a click must open the thread, not a modal with nothing to show.
    ...(task.rowKind === 'outreach' ? { isOutreach: true, senderName: task.senderName } : {}),
    // Tri-state, and the spread is what preserves it: only a boolean crosses, so an absent field on the
    // wire stays ABSENT on the view-model row — "not computed", never false. Same for `lastActivityAt`.
    // The open questions this row is blocked on, carried for the SAME reason the fold keys are: the
    // orange card's population is `bucketOfTask(task) === 'waiting'`, and that verdict now depends on
    // these. A view-model that dropped them would leave the bubble listing a different set of rows than
    // the Tasks screen it peeks at, which is the drift this projection exists to prevent. Absence-
    // preserving, like the two tri-states below it — never `[]`, never `false`.
    ...(task.decisionPrompts?.length ? { decisionPrompts: task.decisionPrompts } : {}),
    ...(task.decisionPromptsUnavailable ? { decisionPromptsUnavailable: true as const } : {}),
    ...(typeof task.runInFlight === 'boolean' ? { runInFlight: task.runInFlight } : {}),
    ...(typeof task.lastActivityAt === 'string' && task.lastActivityAt ? { lastActivityAt: task.lastActivityAt } : {}),
    // The working row's sentence chain: the agent's own status line (empty string means absent), then
    // the first unticked plan step, then the localized "Working on: {title}" at the render site.
    currentAction: task.currentAction?.trim() || undefined,
    plan: task.plan ?? [],
  }));

  return { status: 'ready', data: buildIslandQuickView(tasks, Date.now(), maestroActions) };
}

/** The open quick-view's freshness floor — the same ~20s cadence the task-liveness poll established. */
export const ISLAND_OPEN_POLL_MS = 20_000;

/**
 * A ~20s re-read of the island's two sources — the account-wide tasks list AND the per-workspace
 * aggregate — WHILE the quick-view is open. The card is a live surface when it is up: the duplexer push
 * and the unsettled-list floor still do the ambient work, but a reader looking at "agents at work" rows
 * deserves a bounded staleness regardless of what the push delivers. The timer stops on close and on
 * unmount — every exit path clears it, because the effect owns it.
 */
export function useIslandOpenRefresh(open: boolean): void {
  useEffect(() => {
    if (!open) return undefined;
    const id = window.setInterval(() => {
      void queryClient.refetchQueries({ queryKey: queryKeys.tasks(undefined), exact: true });
      // Prefix match — one refetch covers every workspace's entry.
      void queryClient.refetchQueries({ queryKey: ['workspace-tasks'] });
    }, ISLAND_OPEN_POLL_MS);
    return () => window.clearInterval(id);
  }, [open]);
}
